/**
 * DesignerHost — the 3D designer and the Floor Planner over one document.
 *
 * Owns the floor plan and its undo history, which room the 3D view shows, and
 * whether the planner is open; routes a wall edit from the 3D view back into
 * the plan (`applyRoomEdit`, which carries a window or door on a shared wall
 * across to the other room); asks what to leave where a shared-wall door or
 * window is deleted. Materials are for the whole plan, whichever room is shown.
 *
 * Two hosts use it: the sandbox page (`pages/DesignerSandbox.tsx`, presets and
 * localStorage) and the designer modal on the results page (`Designer3D.tsx`,
 * seeded from — and saved with — the estimate). Each reports every change of
 * the document through `onChange` and decides what to do with it.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, DraftingCompass, Eye, Sofa, Trash2 } from 'lucide-react';
import { DesignerWorkspace, WorkspaceOtherRoom, DesignerCapture } from './DesignerWorkspace';
import { FloorPlanner } from './planner/FloorPlanner';
import { DesignerBrand } from './chrome/DesignerHeader';
import { IconTextButton } from './chrome/IconTextButton';
import { DesignerMenu, DesignerMenuItem } from './chrome/DesignerMenu';
import { SpaceMenu } from './chrome/SpaceMenu';
import { DesignerModal } from './chrome/DesignerModal';
import { DesignerUiState } from './sandboxStorage';
import { DesignerDocument, designerDocument } from './designerDocument';
import { useDesignerHistory } from './useDesignerState';
import { DESIGNER_STRINGS as S } from './strings';
import { getLoadedRetailerId } from '../../data/productTable';
import {
  FloorPlan, PlanRoom, applyRoomEdit, calculateFromPlan, closeWallBehind, findRoom, framingOmissions, openWallBehind, roomDims, roomSurfacesWithOutlines, voidsBehind, withFinish, withStaging,
} from '../../calculations/plan';
import { kitWith, stagePlan, Catalogue, MAX_DENSITY } from '../../calculations/staging';
import { Yard, yardOf } from '../../calculations/plan/yard';
import type { StageVariant } from '../../calculations/staging/stage';
import { FinishPools, pickFinish } from '../ui/room3d/renderFinish';
import { LookPools, pickLook } from '../ui/room3d/renderLooks';
import type { PropDef } from '../../calculations/staging/kits';
import { countAccessories } from '../../calculations/sections/accessories';
import { accessoryDef } from '../../data/slatwallAccessories';
import type { StagedPropView } from '../ui/room3d/stagingMeshes';
import type { PropVariant } from '../props/variants';
import { heldAs, heldOffset } from '../props/propFrame';
import { AccessoryView, WallFace } from '../ui/room3d/types';
import { useAppSettings } from '../../context/AppSettingsContext';
import { RoomSurfaces } from '../../types/sections';

export const NO_UI: DesignerUiState = { lockedFace: null, selectedSectionId: null };

/** `RoomSurfaces.walls` order, as the viewer names the faces. */
const PLAN_FACES_ORDER: WallFace[] = ['front', 'right', 'back', 'left'];

/**
 * How long after the lock drops before the staging is redone: longer than the
 * viewer's trip back into the room (700 ms), which a scene rebuild must never
 * interrupt (see handleUiChange).
 */
const RESTAGE_AFTER_UNLOCK_MS = 1000;

export const designerControlStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.08)', color: '#fff',
  border: '1px solid rgba(255,255,255,0.25)', borderRadius: 6,
  padding: '4px 8px', fontSize: '0.8rem',
};

/**
 * The host's own header controls, as pieces: a host page that gives
 * `headerSlot` as a function lays them out in its own order (and may leave
 * some out); a plain node has them appended after it, as always.
 */
export interface HostControls {
  /** Open the floor planner (3D view only). */
  plannerButton: React.ReactNode;
  /** Which room the 3D view shows (3D view, more than one room). */
  roomShown: React.ReactNode;
  /** The plan's space type. */
  spaceMenu: React.ReactNode;
  /** Stage / stage again (when there are rooms). */
  stage: React.ReactNode;
  /** Clear the staging (when staged). */
  clearStaging: React.ReactNode;
  /** Draw the props' photoreal previews in the 3D view, or the quick stand-ins (when Prop Studio's library is there). */
  previewModels: React.ReactNode;
  /** Live prices or the fallback table. */
  badge: React.ReactNode;
}

/**
 * Whether the 3D view draws the props' photoreal previews (several MB and
 * up to 100k triangles each) or the quick stand-ins: off unless switched
 * on, and remembered in this browser. The previews cost real time on every
 * frame and every rebuild, so they are for checking a staging, not for
 * working in.
 */
const PREVIEW_MODELS_KEY = 'designer.previewModels';
function readPreviewModels(): boolean {
  try { return window.localStorage.getItem(PREVIEW_MODELS_KEY) === '1'; } catch { return false; }
}
function writePreviewModels(on: boolean): void {
  try { window.localStorage.setItem(PREVIEW_MODELS_KEY, on ? '1' : '0'); } catch { /* private mode: not remembered */ }
}
/** The Stage dialog's density (stage.ts: 1 = the kits as they are), remembered on this browser. */
const DENSITY_KEY = 'designer.stageDensity';
function readDensity(): number {
  try {
    const v = Number(window.localStorage.getItem(DENSITY_KEY));
    return Number.isFinite(v) && v > 0 && v <= MAX_DENSITY ? v : 1;
  } catch { return 1; }
}
function writeDensity(d: number): void {
  try { window.localStorage.setItem(DENSITY_KEY, String(d)); } catch { /* private mode: not remembered */ }
}
export type HeaderSlot = React.ReactNode | ((host: HostControls, view: { planner: boolean }) => React.ReactNode);

/** What a host page can do to the document from outside (rename a room, read the plan, open another). */
export interface DesignerHostApi {
  plan: FloorPlan;
  focusRoomId: string | null;
  commit: (plan: FloorPlan, coalesceKey?: string) => void;
  focusRoom: (id: string) => void;
  openPlanner: () => void;
  closePlanner: () => void;
  /**
   * Open another document IN PLACE — the 3D view keeps its WebGL context,
   * shaders and textures and only rebuilds the room (a remount is a cold
   * start of all of that). The undo history starts again; the lock and
   * selection are dropped. `plannerOpen` left out keeps the view the user is in
   * (the planner shows regardless while the document has no room).
   */
  load: (doc: DesignerDocument, opts?: { plannerOpen?: boolean }) => void;
  /** A still + export of the focus room (DesignerWorkspace's capture); null while there is no room to show. */
  capture: DesignerCapture['capture'] | null;
}

export interface DesignerHostProps {
  /** The document to open. Read once, on mount: remount (a new `key`) to replace it. */
  initial: DesignerDocument;
  initialPlannerOpen?: boolean;
  initialUi?: DesignerUiState;
  /** Every change of the document (plan edits, undo, the focus room). */
  onChange?: (doc: DesignerDocument, view: { plannerOpen: boolean; ui: DesignerUiState }) => void;
  title: string;
  /** The 3D view's title, when it differs from the planner's. */
  title3d?: string;
  /** The header's logo (a retailer's, say). Trusscore's when absent. */
  brand?: DesignerBrand;
  /** Extra header controls, before the host's own (presets, reset …) — or a layout of them all. */
  headerSlot?: HeaderSlot;
  headerEnd?: HeaderSlot;
  /** Leave the designer (the modal's close). Absent on the sandbox page. */
  onClose?: () => void;
  /** The most rooms a plan may hold (the planner's Draw tool stops at it). Unlimited when absent. */
  maxRooms?: number;
  /** Whether Escape in the planner, with nothing selected, goes back to 3D (default true). */
  plannerEscapeCloses?: boolean;
  /** Whether the planner's row has its own Back to 3D button (default true). */
  plannerBackButton?: boolean;
  /** The space type a plan without one starts as (the wizard's, say). 'garage' when absent. */
  initialSpaceType?: string;
  /** Set to the host's live API while mounted. */
  apiRef?: React.MutableRefObject<DesignerHostApi | null>;
  /** Seeds staging's placement (an estimate's guid, say): the same seed gives the same rooms. */
  stagingSeed?: string;
  /**
   * The photoreal models Prop Studio has placed for each prop, by prop id:
   * each staged copy of a prop that has any is staged for one of them (at
   * that model's size, for a floor or ceiling prop), drawn as that model's
   * preview in the 3D view, and rendered as that model. Absent, or absent
   * for a prop, the kit's size and the procedural prop apply.
   */
  propVariants?: Record<string, PropVariant[]>;
  /** What Prop Studio says about the props (where each can go, its spaces, props made there): the kits are staged with it. */
  catalogue?: Catalogue;
  /** The floor sets and HDRIs each kind of space may be rendered with: Stage draws one of each for the plan (renderFinish.ts). */
  finishes?: FinishPools;
  /**
   * The render looks by space (Prop Studio's Look Lab). The 3D view shows the
   * one the space's render gets — picked by the staging seed, as the server
   * picks it by the estimate (renderLooks.ts pickLook), or by this visit.
   */
  looks?: LookPools;
  /** The 3D header's look and Photoreal render buttons (default true). Off in Prop Studio's Look Lab, which has its own panel. */
  renderControls?: boolean;
  /** Fill the parent (a positioned box) rather than the whole window over everything: the Look Lab's middle column. */
  embedded?: boolean;
  /** The headers' brand row — logo and title (default true). Off in the Look Lab, whose own page names the room. */
  brandBar?: boolean;
  /** The 3D view's materials readout along the bottom (default true). Off in the Look Lab. */
  readout?: boolean;
}

export const DesignerHost: React.FC<DesignerHostProps> = ({
  initial, initialPlannerOpen = false, initialUi = NO_UI, onChange, title, title3d, brand, headerSlot, headerEnd, onClose,
  maxRooms, plannerEscapeCloses = true, plannerBackButton = true, initialSpaceType, apiRef, stagingSeed, propVariants, catalogue, finishes, looks,
  renderControls = true, embedded = false, brandBar = true, readout = true,
}) => {
  const [ui, setUi] = useState<DesignerUiState>(initialUi);
  const [focusRoomId, setFocusRoomId] = useState<string | null>(initial.focusRoomId);
  const [plannerOpen, setPlannerOpen] = useState(initialPlannerOpen);
  // Counts the documents opened in place (`load`): tells the 3D view a new room
  // has arrived even when it has the same id as the last (every one-room plan's is `room1`).
  const [docKey, setDocKey] = useState(0);
  // The whole plan is one undo history: planner edits and wall edits alike.
  const history = useDesignerHistory<FloorPlan>(initial.plan);

  const plan = history.present;
  // The room the 3D view shows. (Undo can take the remembered one away.)
  const focus = findRoom(plan, focusRoomId) ?? plan.rooms[0] ?? null;
  // With no rooms there is nothing to show in 3D: the planner it is.
  const showPlanner = plannerOpen || !focus;

  // Report changes — but not the mount itself (the document came from the host).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    onChangeRef.current?.(designerDocument(plan, focus?.id ?? null), { plannerOpen: showPlanner, ui });
  }, [plan, focus, showPlanner, ui]);

  const { commit } = history;

  // Another room in the 3D view starts clean: no lock or selection carried over.
  const shownRoomId = focus?.id ?? null;
  const focusRoom = useCallback((id: string) => {
    if (id !== shownRoomId) setUi(NO_UI);
    setFocusRoomId(id);
  }, [shownRoomId]);

  // Leaving edit mode: the staging is done again, so its props hang on whatever
  // accessories the user has put up (or taken down) while editing. The seed is
  // the last one used, so nothing else moves. (`restageRef` is set below, once
  // staging is defined.)
  //
  // RULE — NEVER commit anything (and so never rebuild the 3D scene) at the
  // moment the lock drops. Leaving edit mode sends the camera back to where
  // the user was — INSIDE the room if that is where they locked from — and a
  // scene rebuild during that trip cuts it short and leaves the eye outside.
  // This has been broken twice (a rebuild on every host render, then a restage
  // committed on unlock). The restage waits until the camera has landed.
  const restageRef = useRef<() => void>(() => undefined);
  const restageTimer = useRef<number | null>(null);
  useEffect(() => () => { if (restageTimer.current !== null) window.clearTimeout(restageTimer.current); }, []);
  const lockedRef = useRef<DesignerUiState['lockedFace']>(initialUi.lockedFace);
  const handleUiChange = useCallback((next: DesignerUiState) => {
    const was = lockedRef.current;
    lockedRef.current = next.lockedFace;
    if (was && !next.lockedFace) {
      if (restageTimer.current !== null) window.clearTimeout(restageTimer.current);
      restageTimer.current = window.setTimeout(() => { restageTimer.current = null; restageRef.current(); }, RESTAGE_AFTER_UNLOCK_MS);
    }
    setUi((cur) => (cur.lockedFace === next.lockedFace && cur.selectedSectionId === next.selectedSectionId ? cur : next));
  }, []);

  // A wall edit comes back as the focus room's surfaces; it goes into the plan.
  // What it did to a window or door on a wall shared with another room goes
  // across to that room; if the other room has no place for it, the edit is refused.
  const focusId = focus?.id;
  const { settings } = useAppSettings();
  const margin = settings.openingSafeMarginIn / 12;
  // Deleting a window or door in a shared wall asks what to leave where it stood.
  const [pendingDelete, setPendingDelete] = useState<{ surfaces: RoomSurfaces; coalesceKey?: string; ids: string[] } | null>(null);
  const commitSurfaces = useCallback((surfaces: RoomSurfaces, coalesceKey?: string): void | string => {
    if (!focusId) return undefined;
    const before = findRoom(plan, focusId);
    if (before) {
      const kept = new Set(surfaces.walls.flatMap((w) => (w.openings ?? []).map((o) => o.id)));
      const gone = before.surfaces.walls.flatMap((w) => (w.openings ?? []).map((o) => o.id)).filter((id) => !kept.has(id));
      // A window or door in a wall two rooms share (it has a twin): ask what is left where it stood.
      const shared = before.surfaces.walls.flatMap((w) => (w.openings ?? [])).filter((o) => gone.includes(o.id) && o.kind !== 'passage' && o.link).map((o) => o.id);
      if (shared.length > 0) {
        const trial = applyRoomEdit(plan, focusId, surfaces, margin);
        if (!trial.ok) return trial.reason;
        setPendingDelete({ surfaces, coalesceKey, ids: shared });
        return undefined;
      }
    }
    const result = applyRoomEdit(plan, focusId, surfaces, margin);
    if (!result.ok) return result.reason;
    commit(result.plan, coalesceKey);
    return undefined;
  }, [commit, plan, focusId, margin]);
  const resolveDelete = useCallback((choice: 'leave' | 'close' | 'cancel') => {
    const pending = pendingDelete;
    setPendingDelete(null);
    if (!pending || !focusId || choice === 'cancel') return;
    // Leave an opening: the wall is taken out behind each one first, so what is left is a void.
    let base = plan;
    if (choice === 'leave') {
      for (const id of pending.ids) {
        const opened = openWallBehind(base, focusId, id);
        if (opened.ok) base = opened.plan;
      }
    }
    const result = applyRoomEdit(base, focusId, pending.surfaces, margin);
    if (!result.ok) return;
    const links = choice === 'close' ? voidsBehind(plan, focusId, pending.ids) : [];
    commit(links.reduce(closeWallBehind, result.plan), pending.coalesceKey);
  }, [pendingDelete, plan, focusId, margin, commit]);

  // Materials are for the whole plan, whichever room is on screen.
  const estimate = useMemo(() => calculateFromPlan(plan), [plan]);

  // Staging: the props of the plan's space type, placed in every room. The
  // placement lives in the plan (so it saves, undoes and is pruned with the
  // rooms); the viewer gets each room's props with the kit's definitions.
  const spaceType = plan.spaceType ?? initialSpaceType ?? 'garage';
  const kit = useMemo(() => kitWith(spaceType, catalogue), [spaceType, catalogue]);
  // The space's render look, shown in the 3D view: by the estimate when there is one (the server's pick), else this visit's.
  const visitSeed = useRef(`visit-${Math.random().toString(36).slice(2)}`);
  const look = useMemo(() => pickLook(spaceType, looks, stagingSeed ?? visitSeed.current), [spaceType, looks, stagingSeed]);
  // What staging is told about the props' photoreal models: each one's key and staged size.
  const stageVariants = useMemo(() => {
    if (!propVariants) return undefined;
    const out: Record<string, StageVariant[]> = {};
    for (const [id, list] of Object.entries(propVariants)) {
      out[id] = list.map((v) => {
        // Where the model's box sits in the prop's frame: a floor model's
        // centre off the origin, a hung model's reach either side of the hold
        // and drop below it — so the room is kept clear where the model is.
        const b = v.box;
        const stage = v.model.transform.stage;
        return {
          key: v.key,
          size: v.size,
          perWall: v.model.transform.perWall,
          offset: stage ? undefined : [(b.min[0] + b.max[0]) / 2, (b.min[2] + b.max[2]) / 2],
          reach: stage ? undefined : { left: Math.max(0, -b.min[0]), right: Math.max(0, b.max[0]) },
          drop: stage ? undefined : Math.max(0, -b.min[1]),
        };
      });
    }
    return out;
  }, [propVariants]);
  const setSpaceType = useCallback((type: string) => {
    // A different kind of space: its staging is re-done if there was any.
    const next = withStaging(plan, plan.staging ? stagePlan({ ...plan, spaceType: type }, kitWith(type, catalogue), { seed: stagingSeed, variants: stageVariants, density: densityRef.current }) : undefined, type);
    commit(withFinish(next, next.staging ? pickFinish(type, finishes, stagingSeed) : undefined), 'spaceType');
  }, [plan, commit, stagingSeed, stageVariants, catalogue, finishes]);
  // How full the staging is (stage.ts `density`): the Stage dialog's slider, remembered.
  const [density, setDensity] = useState(readDensity);
  const densityRef = useRef(density);
  densityRef.current = density;
  const changeDensity = useCallback((d: number) => { writeDensity(d); setDensity(d); }, []);
  const [stageOpen, setStageOpen] = useState(false);
  // Each press of Go is a new arrangement (the seed advances); the first is the host's seed itself.
  const stageRound = useRef(0);
  const lastSeedRef = useRef<string | undefined>(stagingSeed);
  const stage = useCallback(() => {
    const round = stageRound.current++;
    const seed = round === 0 ? stagingSeed : `${stagingSeed ?? 'stage'}#${round}`;
    lastSeedRef.current = seed;
    // With the props, the render's finish for this kind of space (a floor set, an HDRI) drawn by the same seed.
    commit(withFinish(withStaging(plan, stagePlan(plan, kit, { seed, variants: stageVariants, density: densityRef.current })), pickFinish(spaceType, finishes, seed)));
  }, [plan, kit, commit, stagingSeed, stageVariants, spaceType, finishes]);
  // Clearing the staging takes its props — and any accessories IT hung — but
  // never the accessories the user put on the walls: those are the room's.
  const clearStaging = useCallback(() => {
    if (plan.staging) commit(withStaging(plan, undefined));
  }, [plan, commit]);
  restageRef.current = () => {
    if (!plan.staging) return;
    commit(withFinish(withStaging(plan, stagePlan(plan, kit, { seed: lastSeedRef.current, variants: stageVariants, density: densityRef.current })), pickFinish(spaceType, finishes, lastSeedRef.current)), 'restage');
  };
  // The model a staged copy was staged for, when the library still has it.
  const variantOf = (def: PropDef, key: string | undefined): PropVariant | undefined => (key ? propVariants?.[def.id]?.find((v) => v.key === key) : undefined);
  // Whether those models' previews are drawn, or the stand-ins (see PREVIEW_MODELS_KEY).
  const [previewModels, setPreviewModels] = useState(readPreviewModels);
  const togglePreviewModels = useCallback(() => setPreviewModels((on) => { writePreviewModels(!on); return !on; }), []);
  const hasLibrary = !!propVariants && Object.keys(propVariants).length > 0;
  // A floor or ceiling prop as the viewer should draw it: at the size it was staged at.
  const sized = (def: PropDef, v: PropVariant | undefined): PropDef => (v ? { ...def, size: v.size } : def);
  // What the 3D view is handed for each room keeps its IDENTITY while that
  // room is unchanged: the viewer rebuilds the scene when any of its inputs
  // is a new object, and an edit to one room must not read as a change to
  // every other room (each rebuilt at full detail with it). So each room's
  // props, accessories, framing omissions and surfaces are remembered here
  // and handed out again while what they were made from is the same.
  const roomMemo = useRef(new Map<string, {
    staging?: FloorPlan['staging']; room?: PlanRoom; kit?: ReturnType<typeof kitWith>; variants?: Record<string, PropVariant[]>; previews?: boolean; props?: StagedPropView[];
    accessoriesRoom?: PlanRoom; accessories?: AccessoryView[];
    omitJson?: string; omit?: ReturnType<typeof framingOmissions>;
    surfacesRoom?: PlanRoom; surfaces?: RoomSurfaces;
  }>());
  const memoOf = (id: string) => {
    let m = roomMemo.current.get(id);
    if (!m) { m = {}; roomMemo.current.set(id, m); }
    return m;
  };
  const propViews = useMemo(() => {
    const byRoom = new Map<string, StagedPropView[]>();
    const staged = plan.staging ?? [];
    // What holds each hung prop, for how far out from the wall it is drawn.
    const holdZ = (s: { hostAccessoryId?: string; roomId: string }): number | undefined => {
      const mine = staged.find((a) => a.kind === 'accessory' && a.id === s.hostAccessoryId);
      if (mine?.accessoryKind) return accessoryDef(mine.accessoryKind).holdAt.z;
      const room = findRoom(plan, s.roomId);
      for (const w of room?.surfaces.walls ?? []) {
        for (const sec of w.sections) {
          const acc = sec.accessories?.find((a) => a.id === s.hostAccessoryId);
          if (acc) return accessoryDef(acc.kind).holdAt.z;
        }
      }
      return undefined;
    };
    for (const s of staged) {
      const list = byRoom.get(s.roomId) ?? [];
      if (s.kind === 'accessory') {
        list.push({ id: s.id, kind: 'accessory', x: s.x, z: s.z, rotation: s.rotation, face: s.face as WallFace | undefined, y: s.y, accessoryKind: s.accessoryKind });
      } else {
        const def = kit.props.find((p) => p.id === s.propId);
        if (!def) continue;
        // The model this copy was staged for: its size for a floor or
        // ceiling prop, and its preview to draw in the stand-in's place.
        const v = variantOf(def, s.variant);
        const off = (s.kind === 'floor' || s.kind === 'yard') && v ? stageVariants?.[def.id]?.find((x) => x.key === v.key)?.offset : undefined;
        // A hung or wall prop's stand-in is the model's box about the hold point, drawn where the model is (propFrame.ts heldAs).
        const held = (s.kind === 'slatwall' || s.kind === 'wall') && v && !v.model.transform.stage ? v.box : undefined;
        list.push({
          id: s.id, def: s.kind === 'floor' || s.kind === 'ceiling' || s.kind === 'yard' ? sized(def, v) : held ? heldAs(def, held) : def, kind: s.kind, place: s.place,
          variant: s.variant, model: previewModels && v ? { model: v.model, parts: v.parts } : undefined,
          bodyOffset: off && (off[0] || off[1]) ? off : held ? heldOffset(held) : undefined,
          x: s.x, z: s.z, rotation: s.rotation, face: s.face as WallFace | undefined, y: s.y, holdZ: s.kind === 'slatwall' ? holdZ(s) : undefined,
        });
      }
      byRoom.set(s.roomId, list);
    }
    // A room's list is the one handed out before while its staging, its room
    // (what hung props hang on), the kit and the library are the same.
    byRoom.forEach((list, roomId) => {
      const m = memoOf(roomId);
      const room = findRoom(plan, roomId);
      if (m.props && m.staging === plan.staging && m.room === room && m.kit === kit && m.variants === propVariants && m.previews === previewModels) byRoom.set(roomId, m.props); // stageVariants follows propVariants
      else { m.staging = plan.staging; m.room = room; m.kit = kit; m.variants = propVariants; m.previews = previewModels; m.props = list; }
    });
    return byRoom;
  }, [plan, kit, propVariants, stageVariants, previewModels]);
  // The yard round the house: the same object while it says the same thing (the viewer rebuilds on a new one).
  const yardRef = useRef<{ yard: Yard; json: string } | null>(null);
  const yard = useMemo(() => {
    const next = yardOf(plan);
    const json = JSON.stringify(next);
    if (yardRef.current && yardRef.current.json === json) return yardRef.current.yard;
    yardRef.current = { yard: next, json };
    return next;
  }, [plan]);
  // The garage doors the staging holds open, by room: those with a car standing part way out through them (the viewer's opener keys).
  const heldOpen = useMemo(() => {
    const byRoom = new Map<string, string[]>();
    for (const s of plan.staging ?? []) {
      if (s.kind !== 'floor' || !s.bay || s.bay.inside >= 1 || s.bay.inside <= 0) continue; // all in, or all out on the driveway: the door stays shut
      const key = s.bay.link ? `link:${s.bay.link}` : `${s.bay.face}:${s.bay.openingId}`;
      byRoom.set(s.roomId, [...(byRoom.get(s.roomId) ?? []), key]);
    }
    return byRoom;
  }, [plan.staging]);
  // The accessories the USER hung, by room: drawn always (in edit mode too), and the ones that count.
  const accessoryViews = useMemo(() => {
    const byRoom = new Map<string, AccessoryView[]>();
    for (const r of plan.rooms) {
      const m = memoOf(r.id);
      if (m.accessories && m.accessoriesRoom === r) { byRoom.set(r.id, m.accessories); continue; }
      const list: AccessoryView[] = [];
      r.surfaces.walls.forEach((w, i) => {
        for (const sec of w.sections) {
          for (const a of sec.accessories ?? []) list.push({ id: a.id, kind: a.kind, face: PLAN_FACES_ORDER[i], x: sec.x + a.x, y: sec.y + a.y });
        }
      });
      if (list.length) byRoom.set(r.id, list);
      m.accessoriesRoom = r;
      m.accessories = list.length ? list : undefined;
    }
    return byRoom;
  }, [plan]);
  const accessories = useMemo(() => countAccessories(plan.rooms.flatMap((r) => r.surfaces.walls)), [plan]);
  // A room's framing omissions, the same object while they say the same thing.
  const framingOmitOf = useCallback((roomId: string) => {
    const m = memoOf(roomId);
    const omit = framingOmissions(plan, roomId);
    const json = JSON.stringify(omit);
    if (m.omit && m.omitJson === json) return m.omit;
    m.omit = omit;
    m.omitJson = json;
    return omit;
  }, [plan]);
  // A room's surfaces with their outlines, the same object while the room is.
  const surfacesOf = useCallback((room: PlanRoom) => {
    const m = memoOf(room.id);
    if (m.surfaces && m.surfacesRoom === room) return m.surfaces;
    m.surfacesRoom = room;
    m.surfaces = roomSurfacesWithOutlines(room);
    return m.surfaces;
  }, []);

  // The 3D view draws the focus room where it has always drawn "the room", and
  // the plan's other rooms around it, each at its offset from the focus room.
  const focusOrigin = useMemo(() => (focus ? { x: focus.x, z: focus.z } : undefined), [focus]);
  const focusFramingOmit = useMemo(() => (focusId ? framingOmitOf(focusId) : undefined), [framingOmitOf, focusId]);
  const otherRoomsRef = useRef<WorkspaceOtherRoom[]>([]);
  const otherRooms = useMemo<WorkspaceOtherRoom[]>(() => {
    const before = otherRoomsRef.current;
    const next: WorkspaceOtherRoom[] = focus
      ? plan.rooms.filter((r) => r.id !== focus.id).map((r) => {
        const entry: WorkspaceOtherRoom = {
          id: r.id,
          offsetX: r.x - focus.x,
          offsetZ: r.z - focus.z,
          dims: roomDims(r),
          surfaces: surfacesOf(r),
          ceilingProfile: r.ceilingProfile,
          framingOmit: framingOmitOf(r.id),
          props: propViews.get(r.id),
          accessories: accessoryViews.get(r.id),
        };
        // The same entry as before when nothing in it has changed.
        const prev = before.find((p) => p.id === r.id);
        const same = prev && prev.offsetX === entry.offsetX && prev.offsetZ === entry.offsetZ
          && prev.dims.length === entry.dims.length && prev.dims.width === entry.dims.width && prev.dims.height === entry.dims.height
          && prev.surfaces === entry.surfaces && prev.ceilingProfile === entry.ceilingProfile
          && prev.framingOmit === entry.framingOmit && prev.props === entry.props && prev.accessories === entry.accessories;
        return same ? prev : entry;
      })
      : [];
    // And the same list, when it is the same entries in the same order.
    const unchanged = next.length === before.length && next.every((e, i) => e === before[i]);
    otherRoomsRef.current = unchanged ? before : next;
    return otherRoomsRef.current;
  }, [plan, focus, propViews, accessoryViews, framingOmitOf, surfacesOf]);
  const openPlanner = useCallback(() => setPlannerOpen(true), []);
  const closePlanner = useCallback(() => setPlannerOpen(false), []);
  const { reset: resetHistory } = history;
  const load = useCallback((doc: DesignerDocument, opts?: { plannerOpen?: boolean }) => {
    resetHistory(doc.plan);
    setFocusRoomId(doc.focusRoomId);
    setUi(NO_UI);
    lockedRef.current = null;
    setPendingDelete(null);
    if (opts?.plannerOpen !== undefined) setPlannerOpen(opts.plannerOpen);
    setDocKey((k) => k + 1);
  }, [resetHistory]);
  // The host page's handle on the document (a rename from its Rooms menu, say).
  const captureRef = useRef<DesignerCapture | null>(null);
  useEffect(() => {
    if (!apiRef) return;
    apiRef.current = {
      plan, focusRoomId: focus?.id ?? null, commit, focusRoom, openPlanner, closePlanner, load,
      capture: captureRef.current ? captureRef.current.capture : null,
    };
  });
  useEffect(() => () => { if (apiRef) apiRef.current = null; }, [apiRef]);
  // The focus room's surfaces as the editor and the 3D view read them: walls
  // with their outlines under a sloped ceiling (the plan strips them on commit).
  const focusSurfaces = useMemo(() => (focus ? surfacesOf(focus) : undefined), [focus, surfacesOf]);

  const retailerId = getLoadedRetailerId();

  // The host's own controls, for both views' header rows — as pieces, so a
  // host page can lay out its own row (`headerSlot` as a function).
  const hostControls: HostControls = {
    plannerButton: !showPlanner ? (
      <IconTextButton icon={<DraftingCompass size={14} />} label={S.planner.open} title={S.planner.openTitle} onClick={openPlanner} />
    ) : null,
    roomShown: !showPlanner && plan.rooms.length > 1 && focus ? (
      <DesignerMenu icon={<Box size={14} />} label={S.focusRoom} title={`${S.focusRoom}: ${focus.name}`}>
        {(close) => plan.rooms.map((r) => (
          <DesignerMenuItem key={r.id} label={r.name} active={r.id === focus.id} onClick={() => { close(); focusRoom(r.id); }} />
        ))}
      </DesignerMenu>
    ) : null,
    spaceMenu: <SpaceMenu value={spaceType} onChange={setSpaceType} />,
    stage: plan.rooms.length > 0 ? (
      <IconTextButton icon={<Sofa size={14} />} label={plan.staging ? S.restage : S.stage} title={S.stageTitle} onClick={() => setStageOpen(true)} />
    ) : null,
    clearStaging: plan.staging ? (
      <IconTextButton icon={<Trash2 size={13} />} label={S.clearStaging} onClick={clearStaging} />
    ) : null,
    previewModels: hasLibrary && plan.staging ? (
      <IconTextButton
        icon={<Eye size={13} />} label={previewModels ? S.previewModelsOn : S.previewModelsOff} title={S.previewModelsTitle}
        active={previewModels} onClick={togglePreviewModels}
      />
    ) : null,
    badge: (
      <span className="dh-badge" style={{ color: retailerId === null ? '#FEB100' : '#9fe0b0' }}>
        {retailerId === null ? S.dataFallback : S.dataRetailer(retailerId)}
      </span>
    ),
  };
  const view = { planner: showPlanner };
  const header = typeof headerSlot === 'function' ? headerSlot(hostControls, view) : (
    <>
      {headerSlot}
      {hostControls.plannerButton}
      {hostControls.roomShown}
      {hostControls.spaceMenu}
      {hostControls.stage}
      {hostControls.clearStaging}
      {hostControls.previewModels}
      {hostControls.badge}
    </>
  );
  const end = typeof headerEnd === 'function' ? headerEnd(hostControls, view) : headerEnd;

  return (
    <div style={embedded ? { position: 'absolute', inset: 0 } : { position: 'fixed', inset: 0, zIndex: 10000 }}>
      {/* The 3D workspace stays MOUNTED while the planner is open over it —
          suspended, not drawn — so coming back to 3D keeps the WebGL context,
          every compiled shader and every texture. Swapping the two in and out
          of the tree made every return to 3D a cold start of all of that. */}
      {focus && (
        <div style={{ position: 'absolute', inset: 0, visibility: showPlanner ? 'hidden' : 'visible' }} aria-hidden={showPlanner}>
          <DesignerWorkspace
            dims={roomDims(focus)}
            room={focusSurfaces ?? focus.surfaces}
            spaceType={spaceType}
            ceilingProfile={focus.ceilingProfile}
            onCommit={commitSurfaces}
            onUndo={history.undo}
            onRedo={history.redo}
            canUndo={history.canUndo}
            canRedo={history.canRedo}
            initialUi={ui}
            onUiChange={handleUiChange}
            onClose={onClose}
            title={plan.rooms.length > 1 ? `${title3d ?? title} · ${focus.name}` : (title3d ?? title)}
            brand={brand}
            estimate={estimate}
            roomKey={`${docKey}:${focus.id}`}
            documentKey={docKey}
            roomOrigin={focusOrigin}
            framingOmit={focusFramingOmit}
            props={propViews.get(focus.id)}
            yard={yard}
            finish={plan.finish}
            look={look}
            heldOpen={heldOpen.get(focus.id)}
            accessoryViews={accessoryViews.get(focus.id)}
            accessories={accessories}
            otherRooms={otherRooms}
            onFocusRoom={focusRoom}
            onOpenPlanner={openPlanner}
            headerSlot={header}
            headerEnd={end}
            suspended={showPlanner}
            captureRef={captureRef}
            renderControls={renderControls}
            brandBar={brandBar}
            readout={readout}
          />
        </div>
      )}
      {(showPlanner || !focus) && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 5 }}>
          <FloorPlanner
            plan={plan}
            onCommit={commit}
            onUndo={history.undo}
            onRedo={history.redo}
            canUndo={history.canUndo}
            canRedo={history.canRedo}
            focusRoomId={focus?.id ?? null}
            onFocusRoom={focusRoom}
            onClose={closePlanner}
            headerSlot={header}
            headerEnd={end}
            brand={brand}
            brandBar={brandBar}
            maxRooms={maxRooms}
            escapeCloses={plannerEscapeCloses}
            backButton={plannerBackButton}
            spaceType={spaceType}
            onSpaceType={setSpaceType}
          />
        </div>
      )}
      {stageOpen && (
        <DesignerModal title={S.stageDialog.title} onClose={() => setStageOpen(false)} width={400} icon={<Sofa size={16} color="#FEB100" />}>
          <p>{S.stageDialog.body}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
            <span>{S.stageDialog.sparse}</span>
            <input
              type="range" min={0} max={MAX_DENSITY} step={0.25} value={density} aria-label={S.stageDialog.density}
              onChange={(e) => changeDensity(Number(e.target.value))} style={{ flex: 1, accentColor: '#FEB100' }}
            />
            <span>{S.stageDialog.dense}</span>
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 12 }}>{S.stageDialog.readout(density)}</p>
          <div className="dh-modal-actions">
            <button type="button" className="dh-btn is-quiet" onClick={() => setStageOpen(false)}>{S.stageDialog.close}</button>
            <button type="button" autoFocus className="dh-btn is-accent" onClick={stage}>{S.stageDialog.go}</button>
          </div>
        </DesignerModal>
      )}
      {pendingDelete && (
        <DesignerModal title={S.voidPrompt.title} dismissable={false} onClose={() => resolveDelete('cancel')}>
          <p>{S.voidPrompt.body(pendingDelete.ids.length)}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button type="button" autoFocus className="dh-btn is-accent" style={{ height: 34 }} onClick={() => resolveDelete('leave')}>{S.voidPrompt.leave}</button>
            <button type="button" className="dh-btn" style={{ height: 34 }} onClick={() => resolveDelete('close')}>{S.voidPrompt.close}</button>
            <button type="button" className="dh-btn is-quiet" style={{ height: 34 }} onClick={() => resolveDelete('cancel')}>{S.voidPrompt.cancel}</button>
          </div>
        </DesignerModal>
      )}
    </div>
  );
};

export default DesignerHost;
